import { useTheme } from "@emotion/react"
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from "@mui/material"
import ImageUploadField from "components/FileUpload/ImageUploadField"
import AppTextArea from "components/StyledComponents/AppTextArea"
import AppTextField from "components/StyledComponents/AppTextField"
import React, { forwardRef, useImperativeHandle, useRef, useState } from "react"
import { Col, Row } from "react-bootstrap"
import styles from "../../../dmvmenu.module.scss"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import AppButton from "components/StyledComponents/AppButton"
import PlayArrow from "@mui/icons-material/PlayArrowRounded"
import Pause from "@mui/icons-material/PauseRounded"
import { generateSoundbitesAction } from "redux/marketing/actions"
import { useDispatch, useSelector } from "react-redux"

const BasicDetailsForm = forwardRef(({ formik, onImageUpload, handleImageDelete }, ref) => {
  const theme = useTheme()
  const audioRef = useRef(null)
  const dispatch = useDispatch()
  const [isExpanded, setIsExpanded] = useState(true)
  const [isPlaying, setIsPlaying] = useState(false)
  const { soundbites } = useSelector((state) => state.marketing)

  const handleSoundbite = (soundbite) => {
    const payload = {
      text: soundbite
    }
    dispatch(generateSoundbitesAction(payload))
    formik.setFieldTouched(`soundbite`, false)
  }

  const toggleSoundbite = () => {
    const soundbite_url = soundbites?.url || formik.values?.soundbite_url

    if (!soundbite_url) return

    // If already playing, pause it
    if (isPlaying && audioRef.current) {
      audioRef.current.pause()
      setIsPlaying(false)
    } else {
      // If audio exists, reset it
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
      }

      // Create new audio and play
      audioRef.current = new Audio(soundbite_url)
      audioRef.current.play()
      setIsPlaying(true)

      // When audio ends, reset state
      audioRef.current.onended = () => {
        setIsPlaying(false)
      }
    }
  }

  useImperativeHandle(ref, () => ({
    stopAudio: () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
        audioRef.current = null
      }
    }
  }))

  return (
    <Box
      flexDirection="column"
      // className="mt-4"
      className={styles.accordian_container}
      sx={{
        backgroundColor: isExpanded
          ? theme.palette.background.main
          : theme.palette.background.default,
        paddingBottom: !isExpanded && "0px !important",
        margin: isExpanded && "36px 0px !important"
      }}>
      <Accordion expanded={isExpanded} onChange={() => setIsExpanded(!isExpanded)}>
        <AccordionSummary className={styles.summary}>
          <Box display="flex" alignItems="center" gap={1}>
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            <Typography variant="bodyLyon">Menu Item</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box className=" w-100 py-2">
            <Row className="w-100 m-0">
              <Col lg="6">
                <AppTextField
                  id="region"
                  name="region"
                  isUpperCase={!false}
                  type="text"
                  fullWidth
                  label="Region Name"
                  isRequired={true}
                  placeholder="Enter Region Name"
                  value={formik.values.region}
                  onChange={formik.handleChange}
                  error={formik.touched.region && Boolean(formik.errors.region)}
                  helperText={formik.errors.region}
                />
              </Col>
              <Col lg="6">
                <ImageUploadField
                  label="Upload Thumbnail"
                  isVariant={true}
                  placeholder="Upload Image"
                  value={formik.values.uploadThumbnail}
                  onChange={(e) => onImageUpload(e)}
                  onDelete={() => handleImageDelete(formik)}
                  error={formik.errors.uploadThumbnail}
                  touched={formik.touched.uploadThumbnail}
                />
              </Col>
            </Row>
            <Row className="w-100 m-0 mt-4">
              <Col lg="12">
                <AppTextField
                  id="Place"
                  placeholder="Enter Geographic Location"
                  label="Geographic Location"
                  fullWidth
                  isUpperCase={true}
                  value={formik.values.Place}
                  onChange={formik.handleChange}
                  error={formik.touched.Place && Boolean(formik.errors.Place)}
                  helperText={formik.errors.Place}></AppTextField>
              </Col>
            </Row>{" "}
            <Row className="w-100 m-0 mt-4">
              <Col lg="12">
                <AppTextField
                  id="terroir"
                  placeholder="Enter Terroir"
                  label="Terroir"
                  fullWidth
                  isUpperCase={true}
                  value={formik.values.terroir}
                  onChange={formik.handleChange}
                  error={formik.touched.terroir && Boolean(formik.errors.terroir)}
                  helperText={formik.errors.terroir}></AppTextField>
              </Col>
            </Row>
            <Row className="w-100 m-0 mt-4">
              <Col lg="12">
                <AppTextField
                  id="famous_vineyards"
                  placeholder="Enter Famous Vineyards"
                  label="Famous Vineyards"
                  fullWidth
                  isUpperCase={true}
                  value={formik.values.famous_vineyards}
                  onChange={formik.handleChange}
                  error={formik.touched.famous_vineyards && Boolean(formik.errors.famous_vineyards)}
                  helperText={formik.errors.famous_vineyards}></AppTextField>
              </Col>
            </Row>
            <Row className="w-100 m-0 mt-4">
              <Col lg="12">
                <AppTextField
                  id="unique_fact"
                  placeholder="Enter Overview"
                  label="Overview"
                  fullWidth
                  isUpperCase={true}
                  value={formik.values.unique_fact}
                  onChange={formik.handleChange}
                  error={formik.touched.unique_fact && Boolean(formik.errors.unique_fact)}
                  helperText={formik.errors.unique_fact}></AppTextField>
              </Col>
            </Row>
            <Row className="w-100 m-0 mt-4">
              <Col lg="12">
                <AppTextArea
                  id="famous_wines"
                  placeholder="Enter Most Famous Wines"
                  label={"Most Famous Wines"}
                  isUpperCase={true}
                  minRows={4}
                  maxRows={15}
                  error={formik.errors?.famous_wines}
                  touched={formik.errors?.famous_wines}
                  value={formik.values.famous_wines}
                  onChange={formik.handleChange("famous_wines")}></AppTextArea>
              </Col>
            </Row>
            <Row className="w-100 m-0 mt-4">
              <Col lg="12">
                <AppTextArea
                  id="white_grape_varietals"
                  label="White Grape Varietals"
                  minRows={5}
                  maxRows={15}
                  placeholder="White Grape Varietals"
                  value={formik.values.white_grape_varietals}
                  onChange={formik.handleChange("white_grape_varietals")}
                  error={formik.errors?.white_grape_varietals}
                  touched={formik.errors?.white_grape_varietals}
                />
              </Col>
            </Row>
            <Row className="w-100 m-0 mt-4">
              <Col lg="12">
                <AppTextArea
                  id="red_grape_varietals"
                  label="Red Grape Varietals"
                  minRows={5}
                  maxRows={15}
                  placeholder="Red Grape Varietals"
                  value={formik.values.red_grape_varietals}
                  onChange={formik.handleChange("red_grape_varietals")}
                  error={formik.errors?.red_grape_varietals}
                  touched={formik.errors?.red_grape_varietals}
                />
              </Col>
            </Row>
            <Row className="w-100 m-0 mt-4">
              <Col lg="12">
                <AppTextArea
                  id="menuPairing"
                  label="Food Pairing"
                  minRows={3}
                  maxRows={15}
                  placeholder="Food Pairing"
                  value={formik.values.menuPairing}
                  onChange={formik.handleChange("menuPairing")}
                  error={formik.errors?.menuPairing}
                  touched={formik.errors?.menuPairing}
                />
              </Col>
            </Row>
            <Row className="w-100 m-0 mt-4">
              <Col lg="12">
                <AppTextArea
                  label="Soundbite"
                  minRows={5}
                  maxRows={15}
                  placeholder={`Enter Soundbite`}
                  value={formik.values?.soundbite}
                  onChange={formik.handleChange("soundbite")}
                  onBlur={formik.handleBlur("soundbite")}
                  error={Boolean(formik.errors?.soundbite)}
                  touched={formik.touched?.soundbite}
                  helperText={formik.errors?.soundbite}
                />
              </Col>
              <Row className="w-100 m-0 mt-2 justify-content-end align-items-center">
                <Col lg="3" className="d-flex justify-content-end p-0">
                  {(soundbites?.url || formik.values?.soundbite_url) &&
                  !formik.touched?.soundbite ? (
                    <AppButton styles={{ padding: "9px 16px" }} onClick={() => toggleSoundbite()}>
                      {isPlaying ? (
                        <>
                          <Pause
                            sx={() => ({ height: "28px", width: "28px", marginRight: "6px" })}
                          />
                          Stop
                        </>
                      ) : (
                        <>
                          <PlayArrow sx={{ height: "28px", width: "28px", marginRight: "6px" }} />
                          Play
                        </>
                      )}
                    </AppButton>
                  ) : null}
                </Col>
                <Col lg="3" className="d-flex justify-content-end p-0">
                  <AppButton onClick={() => handleSoundbite(formik.values?.soundbite)}>
                    Generate Sound Bite
                  </AppButton>
                </Col>
              </Row>
            </Row>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  )
})

BasicDetailsForm.displayName = "BasicDetailsForm"

export default BasicDetailsForm
